/**
 * Knob registry (FXC-9009 design-program phase 2).
 *
 * The canonical list of adjustable design knobs the workbench exposes for fixed/free + limits.
 * Each knob has display metadata, a sensible default range, and a `read(ctx)` to show its live
 * value. The free/fixed + min/max a user sets here are the design space for the Sweep workbench
 * (free knobs = sweep axes) and the Inverse screen (free knobs = optimizer variables).
 *
 * v1 covers the operating-point knobs (conditions + deployment + blowing) — the knobs you actually
 * sweep/optimize. Geometry control points are a later (advanced) group; the model below extends to
 * them unchanged (add KnobDef rows).
 */
import type { HighLiftAirfoil } from "../geometry/estolConfig";

export interface KnobConstraint {
  /** When true the knob is held at its current value (not a sweep axis / optimizer variable). */
  fixed: boolean;
  /** Lower/upper bound for the knob when free. Undefined = use the registry default range. */
  min?: number;
  max?: number;
}

export type KnobGroup = "Condition" | "Operation" | "Geometry";

export interface KnobCtx {
  alphaDeg: number;
  flowConditions: { mach: number; reynolds: number };
  config: HighLiftAirfoil;
}

export interface KnobDef {
  /** Stable key — also the flat-axis key for config knobs (matches applyAxesToConfig). */
  key: string;
  label: string;
  group: KnobGroup;
  /** Default range (used as the limits until the user narrows them). */
  min: number;
  max: number;
  step: number;
  decimals: number;
  unit?: string;
  /** Live value for display. */
  read: (ctx: KnobCtx) => number;
}

export const KNOB_REGISTRY: KnobDef[] = [
  { key: "alpha_deg", label: "Angle of attack", group: "Condition", min: -5, max: 25, step: 0.5, decimals: 1, unit: "°", read: (c) => c.alphaDeg },
  { key: "mach", label: "Mach", group: "Condition", min: 0.05, max: 0.4, step: 0.01, decimals: 2, read: (c) => c.flowConditions.mach },
  { key: "reynolds", label: "Reynolds", group: "Condition", min: 1e6, max: 2e7, step: 1e6, decimals: 0, unit: "Re", read: (c) => c.flowConditions.reynolds },
  { key: "flap_deploy_deg", label: "Flap deploy", group: "Operation", min: 0, max: 45, step: 1, decimals: 0, unit: "°", read: (c) => c.config.operation.deployAngleDeg },
  { key: "flaperon_deflection_deg", label: "Flaperon", group: "Operation", min: -20, max: 30, step: 1, decimals: 0, unit: "°", read: (c) => c.config.operation.flaperonAngleDeg },
  { key: "disk_thrust_loading", label: "Blowing Cμ", group: "Operation", min: 0, max: 1, step: 0.05, decimals: 2, read: (c) => c.config.operation.diskLoading },
  // Geometry design params — flat-axis keys (applyAxesToConfig). Rigging via the deploy-hinge offset
  // (overlap ≈ x, gap ≈ z) + per-element shape modes. Bounds are perturbation windows around the
  // default config. These are the geometry axes the Tailor surface axis-selector offers; each is a
  // composite-DB identity axis (see composite_db/schema.py). The NN-conditioned / DOE-varied SUBSET is
  // nn_features.py GEOM_KEYS (doeGen.ts GEOM) — a few of these that we have generated data for.
  // --- Deploy rigging (flap-to-main slot) ---
  // Reads are NULL-SAFE (optional chaining → NaN when a config omits a nested field) so a knob never
  // throws while rendering the knob panel or computing the Tailor surface's held-axis set — a loaded
  // design that lacks e.g. flaperonHinge/vane must degrade, not white-screen (FXC-9009).
  { key: "deploy_hinge_x", label: "Flap overlap", group: "Geometry", min: 0.66, max: 0.76, step: 0.005, decimals: 3, unit: "c", read: (c) => c.config.design?.deployHinge?.pivot?.[0] ?? NaN },
  { key: "deploy_hinge_z", label: "Flap gap", group: "Geometry", min: -0.25, max: -0.15, step: 0.005, decimals: 3, unit: "c", read: (c) => c.config.design?.deployHinge?.pivot?.[1] ?? NaN },
  { key: "flaperon_hinge_x", label: "Flap hinge x", group: "Geometry", min: 0.71, max: 0.81, step: 0.005, decimals: 3, unit: "c", read: (c) => c.config.design?.flaperonHinge?.pivot?.[0] ?? NaN },
  { key: "flaperon_hinge_z", label: "Flap hinge z", group: "Geometry", min: -0.10, max: 0.00, step: 0.005, decimals: 3, unit: "c", read: (c) => c.config.design?.flaperonHinge?.pivot?.[1] ?? NaN },
  // --- Main element: cove shaping + trailing-edge bluntness ---
  { key: "cove_back_x", label: "Main cove/slot", group: "Geometry", min: 0.50, max: 0.62, step: 0.005, decimals: 3, unit: "c", read: (c) => c.config.design?.cove?.coveX ?? NaN },
  { key: "cove_lower_cut_x", label: "Cove lower cut", group: "Geometry", min: 0.60, max: 0.72, step: 0.005, decimals: 3, unit: "c", read: (c) => c.config.design?.cove?.lowerCutX ?? NaN },
  { key: "cove_upper_cut_x", label: "Cove upper cut", group: "Geometry", min: 0.78, max: 0.86, step: 0.005, decimals: 3, unit: "c", read: (c) => c.config.design?.cove?.upperCutX ?? NaN },
  { key: "blunt_thickness", label: "TE thickness", group: "Geometry", min: 0.001, max: 0.006, step: 0.0005, decimals: 4, unit: "c", read: (c) => c.config.main?.bluntThickness ?? NaN },
  // --- Vane (mid element) position/shape ---
  { key: "vane_te_x", label: "Vane TE x", group: "Geometry", min: 0.70, max: 0.80, step: 0.005, decimals: 3, unit: "c", read: (c) => c.config.design?.vane?.te?.[0] ?? NaN },
  { key: "vane_te_z", label: "Vane TE z", group: "Geometry", min: 0.02, max: 0.09, step: 0.005, decimals: 3, unit: "c", read: (c) => c.config.design?.vane?.te?.[1] ?? NaN },
  // --- Flaperon (aft element) nose + cut shaping ---
  { key: "flaperon_nose_tip_z", label: "Flap nose camber", group: "Geometry", min: 0.045, max: 0.105, step: 0.005, decimals: 3, unit: "c", read: (c) => c.config.design?.flaperon?.noseTip?.[1] ?? NaN },
  { key: "flaperon_nose_tip_x", label: "Flap nose x", group: "Geometry", min: 0.69, max: 0.74, step: 0.005, decimals: 3, unit: "c", read: (c) => c.config.design?.flaperon?.noseTip?.[0] ?? NaN },
  { key: "flaperon_lower_cut_x", label: "Flap lower cut", group: "Geometry", min: 0.71, max: 0.79, step: 0.005, decimals: 3, unit: "c", read: (c) => c.config.design?.flaperon?.lowerCutX ?? NaN },
  { key: "flaperon_upper_cut_x", label: "Flap upper cut", group: "Geometry", min: 0.86, max: 0.94, step: 0.005, decimals: 3, unit: "c", read: (c) => c.config.design?.flaperon?.upperCutX ?? NaN },
  // Vane FOLLOWER linkage (FXC-9009 double-slotted tranche): the vane rides the deploy track as a
  // follower of the flap. gain<1 => the vane deploys less than the flap; lag => it trails a slot-gap
  // behind along the track. Read the identity (gain 1 / lag 0 = rigid assembly) when no follower is
  // declared, so the default config reads unchanged and these stay opt-in DOFs.
  { key: "vane_follower_gain", label: "Vane follower gain", group: "Geometry", min: 0, max: 1, step: 0.05, decimals: 2, read: (c) => c.config.design?.vaneFollower?.gain ?? 1 },
  { key: "vane_follower_lag", label: "Vane follower lag", group: "Geometry", min: 0, max: 0.1, step: 0.005, decimals: 3, unit: "c", read: (c) => c.config.design?.vaneFollower?.lagChords ?? 0 },
];

export const KNOB_GROUPS: KnobGroup[] = ["Condition", "Operation", "Geometry"];

/** The effective [min,max] for a knob = the user's bounds if set, else the registry default range. */
export function knobBounds(def: KnobDef, c?: KnobConstraint): [number, number] {
  const rawMin = c?.min;
  const rawMax = c?.max;
  const min = typeof rawMin === "number" && Number.isFinite(rawMin) ? rawMin : def.min;
  const max = typeof rawMax === "number" && Number.isFinite(rawMax) ? rawMax : def.max;
  return min <= max ? [min, max] : [max, min];
}

/** Clamp a value to a knob's effective bounds. */
export function clampToKnob(def: KnobDef, c: KnobConstraint | undefined, raw: number): number {
  const [lo, hi] = knobBounds(def, c);
  return Math.min(hi, Math.max(lo, raw));
}
